export type Visibility =
  | "draft"
  | "private"
  | "unlisted"
  | "public"
  | "under_review"
  | "hidden";

export const localMvpOwnerId = "local_user";
export const systemOwnerId = "system";

export type AssetType =
  | "cover"
  | "portrait"
  | "background"
  | "music"
  | "ending_image"
  /** A one-shot or looping sound effect, fired from a scene section's cue. */
  | "sound"
  /** A full-frame still that replaces the stage for one scene section. */
  | "illustration"
  /** A full-frame video that replaces the stage for one scene section. */
  | "video";

export type AssetStatus =
  | "pending"
  | "generating"
  | "ready"
  | "failed"
  | "deleted";

export type GenerationJobStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

export type GenerationStep =
  | "prompt_expansion"
  | "story_bible_generation"
  | "character_generation"
  | "story_graph_generation"
  | "scene_writing"
  | "semantic_narrative_review"
  | "build_playable_novel";

export interface Asset {
  id: string;
  type: AssetType;
  url: string;
  prompt: string;
  status: AssetStatus;
  /**
   * Optional human-readable label. Used by the editor's Locations tab to name a
   * `background` asset (a "location"); absent on generated assets, where the UI
   * falls back to a derived name. Passes through validation/migration untouched.
   */
  name?: string;
}

/**
 * Character facial expressions. `neutral` is the base portrait
 * (`Character.portrait_asset_id`); the rest are optional variant sprites
 * generated by editing the neutral portrait, selected per scene.
 */
export const emotions = [
  "neutral",
  "happy",
  "sad",
  "angry",
  "afraid",
  "surprised",
  "tired",
  "worried",
  "embarrassed",
  "disgusted",
  "disappointed",
  "relieved",
  "confused"
] as const;
export type Emotion = (typeof emotions)[number];

/**
 * The fixed palette of character traits, picked in the editor like genres (a
 * chip multi-select). A character's `traits` are a subset of these; the LLM is
 * asked to choose from this list too, so editor and generation stay aligned.
 */
export const characterTraits = [
  "Ambitious",
  "Cheerful",
  "Loyal",
  "Cynical",
  "Brave",
  "Shy",
  "Hot-headed",
  "Kind",
  "Manipulative",
  "Curious",
  "Stubborn",
  "Gloomy",
  "Playful",
  "Serious",
  "Romantic",
  "Cold",
  "Optimistic",
  "Paranoid",
  "Charismatic",
  "Reserved",
  "Reckless",
  "Meticulous",
  "Generous",
  "Ruthless",
  "Witty",
  "Naive",
  "Proud",
  "Compassionate",
  "Secretive",
  "Impulsive"
] as const;
export type CharacterTrait = (typeof characterTraits)[number];

/** Gender options offered in the editor (stored as a plain string). */
export const characterGenders = ["female", "male", "non-binary"] as const;

export interface Character {
  id: string;
  name: string;
  /** Free string; the editor offers `characterGenders` as quick choices. */
  gender: string;
  age: number;
  /** A subset of `characterTraits` (capped at `mvpLimits.maxTraits`). */
  traits: string[];
  /** Appearance description — the prompt used to generate the portrait image. */
  appearance: string;
  portrait_asset_id: string;
  /**
   * Optional expression variants: emotion → portrait asset id. `neutral`, when
   * present, mirrors `portrait_asset_id`. Missing emotions fall back to the
   * neutral portrait at render time. Absent on novels without emotion sprites.
   */
  emotion_portraits?: Partial<Record<Emotion, string>>;
}

export type StoryStateValue = string | number | boolean | string[];
export type StoryState = Record<string, StoryStateValue>;
export type NumericRequirement = {
  op?: "gte" | "gt" | "lte" | "lt" | "eq";
  value: number;
};
export type StateRequirementValue = StoryStateValue | NumericRequirement;
export type StateRequirement = Record<string, StateRequirementValue>;

export interface Choice {
  id: string;
  text: string;
  next_scene_id: string;
  /** World-state changes applied when this choice is selected. */
  effects?: StoryState;
  /** Conditions that must be satisfied for this choice to be available. */
  required_state?: StateRequirement;
}

/**
 * A mid-scene expression change. As the reader steps through the scene's text,
 * the character's sprite switches to `emotion` starting at the beat that contains
 * `anchor_text` — a short verbatim excerpt copied from the scene's `text` marking
 * the moment the feeling shifts (e.g. the line where a character hears the news).
 * Purely presentational: the player locates the anchor and ignores the cue if it
 * cannot be found, so a stale anchor never breaks playback.
 */
export interface SceneEmotionCue {
  char_id: string;
  emotion: Emotion;
  anchor_text: string;
}

/** How a section is separated from the previous one in the scene's prose. */
export type SectionBreak = "line" | "paragraph";

/** Kinds of per-section cue. New kinds extend this union (and validation). */
export const sectionCueKinds = ["sound", "frame"] as const;
export type SectionCueKind = (typeof sectionCueKinds)[number];

/**
 * Plays an audio asset (`sound` or `music`) when the reader reaches the
 * section. A looping cue stops when the reader leaves the section.
 */
export interface SectionSoundCue {
  id: string;
  kind: "sound";
  asset_id: string;
  /** 0..1, default 1. */
  volume?: number;
  loop?: boolean;
}

/**
 * Replaces the whole stage (background + characters) for this section with a
 * full-frame image or video asset — a flashback still, a letter close-up, a
 * cutscene. The section's text still shows in the reading panel.
 */
export interface SectionFrameCue {
  id: string;
  kind: "frame";
  asset_id: string;
}

export type SectionCue = SectionSoundCue | SectionFrameCue;

/**
 * One reading beat of a scene — the unit the player reveals per "Next" tap,
 * roughly one sentence. A scene's `sections` are its timeline: each frame says
 * what the stage shows while that text is on screen.
 *
 * `text` of all sections, joined with their `break_before` separators, is the
 * scene's `text`. When the two drift apart (an AI rewrite or a plain text edit
 * changed `text`), readers re-segment `text` and carry each frame's staging
 * over by matching sentence text — see `resolveSceneSections`.
 */
export interface SceneSection {
  /** Unique within the scene. */
  id: string;
  text: string;
  /** Separator before this section in the scene's prose (absent = a space). */
  break_before?: SectionBreak;
  /**
   * Who is on stage for this beat, left to right — at most
   * `mvpLimits.maxSectionCharacters` (the two characters the moment focuses
   * on). A subset of the scene's `characters_present`; empty = only the
   * location is shown.
   */
  characters: string[];
  /** Expression override per on-stage character; absent → the scene's emotion timeline. */
  emotions?: Record<string, Emotion>;
  /** Location for this beat; absent → the scene's `background_asset_id`. */
  background_asset_id?: string;
  /** Extra media fired or shown at this beat (sounds, full-frame replacements). */
  cues?: SectionCue[];
}

export interface Scene {
  id: string;
  title: string;
  background_asset_id: string;
  music_asset_id: string;
  characters_present: string[];
  /**
   * Optional per-character expression at the START of this scene (the first
   * beat): charId → emotion. Keys are a subset of `characters_present`;
   * missing/absent entries render as neutral. `emotion_cues` may change the
   * expression at later beats within the same scene.
   */
  character_emotions?: Record<string, Emotion>;
  /**
   * Optional mid-scene expression changes, in reading order. Each cue switches a
   * character's sprite from the beat containing its `anchor_text` onward; a later
   * cue for the same character supersedes an earlier one. Before a character's
   * first matching cue it shows its `character_emotions` entry (or neutral).
   * Absent on novels without mid-scene emotion changes.
   */
  emotion_cues?: SceneEmotionCue[];
  text: string;
  /**
   * Optional per-beat timeline (see `SceneSection`). `characters_present` is
   * the scene's roster — everyone who appears at SOME beat. Absent on older
   * novels: readers derive a timeline from `text` (`resolveSceneSections`).
   */
  sections?: SceneSection[];
  choices: Choice[];
  is_ending: boolean;
  /** World-state changes applied when the player enters this scene. */
  state_changes?: StoryState;
  /**
   * True when this scene was appended by a continuation ("догенерация") pass
   * rather than the original one-shot generation. Purely informational — the
   * runtime treats it like any other scene; the editor uses it to mark added
   * content. Absent on original scenes.
   */
  is_continuation?: boolean;
}

export interface Ending {
  id: string;
  scene_id: string;
  title: string;
  type: "good" | "bad" | "neutral";
  description: string;
  /**
   * Whether this ending may be used as a continuation ("догенерация") point.
   * Undefined is treated as continuable — only an explicit `false` locks an
   * ending as final and blocks the extend endpoint from growing past it.
   */
  can_continue?: boolean;
}

export interface NovelGenerationInput {
  idea: string;
  language: string;
  tone: string;
  /**
   * The author's chosen genre from the Create form ("Drama", "Mystery", …), when
   * they picked one. Threaded into generation as an authoritative genre and stored
   * so a whole-novel rebuild keeps it. Absent → the model infers the genre itself.
   */
  genre?: string;
  /**
   * The author's chosen story length. Selects the graph size preset
   * (short → SMALL ~12–13 scenes, medium → MEDIUM ~15, long → RICH ~18–22) and is
   * stored so a whole-novel rebuild keeps it. Absent → treated as medium.
   */
  length?: "short" | "medium" | "long";
  /**
   * The author's raw art-style note ("аниме", "watercolour"), as typed. Shown
   * and edited in the editor's style field; re-expanded when the author asks to
   * regenerate all images. Absent on novels generated before the art-style feature.
   */
  art_style_notes?: string;
  /**
   * The ONE expanded art-style descriptor applied to every image in the novel.
   * Used verbatim by the image-regeneration routes so a regenerated asset matches
   * the rest. Absent on pre-feature novels (those recover the style from prompts).
   */
  art_style?: string;
}

export interface NovelContinuityReview {
  /** Number of hard-error continuity issues found in the story graph. */
  graph_issue_count: number;
  /** Number of hard-error continuity issues found in scene texts. */
  scene_issue_count: number;
  /** How many times the story graph was re-generated due to continuity failures. */
  repaired_graph_attempts: number;
  /** How many times the scene texts were re-generated due to continuity failures. */
  repaired_scene_attempts: number;
}

/**
 * One semantic narrative issue as recorded in a novel's generation trace.
 * Structurally mirrors the API's `SemanticNarrativeIssue` — the domain package
 * depends on nothing, so the shape is restated here rather than imported.
 */
export interface NovelSemanticIssueTrace {
  severity: "warning" | "error";
  code: string;
  sceneId?: string;
  message: string;
  branchPath?: string[];
}

/**
 * The models actually RESOLVED for each class of call in a run, after every
 * fallback has been applied. `model` / `prose_model` above only say what was
 * configured and leave it ambiguous which model did what — so a run's cost and
 * quality could not be attributed to a model after the fact. Every field here is
 * a concrete model id (never undefined-meaning-"same as the main one"), so a
 * trace answers "which model wrote the prose / polished it / repaired it".
 */
export interface NovelResolvedModels {
  /** Structural stages: prompt expansion, bible, graph repair, narrative repair. */
  structural: string;
  /** Character list and story-graph/branch calls (may equal `structural`). */
  structured: string;
  /** Initial scene prose (scene_writing). */
  prose: string;
  /** The holistic editorial polish pass. */
  polish: string;
  /** Continuity / semantic / graph repair passes. */
  repair: string;
}

/**
 * Outcome of the editorial polish pass and its post-polish regression gate.
 *
 * The polish runs LAST and on the cheapest/weakest prose model, so it can undo
 * what the (paid, strong-model) continuity and semantic repairs just fixed. The
 * gate re-runs the deterministic semantic review on the polished manuscript and
 * throws the patch away when it made things worse — this records that decision
 * with both sides' counts, so a run can be audited without re-running anything.
 */
export interface NovelEditorialPolishTrace {
  /**
   * "kept" — the polish ran and did not regress the review (patch merged).
   * "discarded" — the polish regressed the review; the unpolished texts shipped.
   * "skipped_clean" — polishSkipWhenClean was on and the review was error-free.
   * "disabled" — the pass is switched off (LOREVOK_EDITORIAL_POLISH=false).
   */
  decision: "kept" | "discarded" | "skipped_clean" | "disabled";
  /** Semantic-review error count BEFORE the polish. */
  errors_before: number;
  /** Semantic-review error count AFTER the polish (equals before when it did not run). */
  errors_after: number;
  /**
   * Issue count (errors + warnings) before the polish — the gate's tiebreak, so it
   * is the COMPARABLE count: `unintroduced_person` is excluded, because the polish
   * prompt deliberately creates that one.
   */
  issues_before: number;
  /** The same comparable issue count after the polish. */
  issues_after: number;
  /** How many scene texts the polish patch actually changed (0 when it did not run). */
  scenes_revised: number;
}

export interface NovelGenerationTrace {
  provider: string;
  model?: string;
  /**
   * Prose model configured for scene writing when it differs from `model`
   * (e.g. a faster model routed only to initial scene prose). Absent when prose
   * shares the main model. Makes a run self-documenting about prose routing.
   */
  prose_model?: string;
  /**
   * The model id resolved for each class of call. Unlike `prose_model`, these are
   * always populated and never collapse to "same as `model`", so dev/prod model
   * routing is unambiguous in a stored novel. Absent on providers that do not
   * report it (mock, and novels generated before this field existed).
   */
  resolved_models?: NovelResolvedModels;
  /**
   * Total output tokens across the pipeline's LLM calls, for checking a run
   * against the provider's output-tokens-per-minute tier. Absent on providers
   * that do not report usage.
   */
  output_tokens_total?: number;
  prompt_expansion?: {
    title: string;
    description: string;
    genre: string[];
  };
  /**
   * Story Bible produced during the story_bible_generation stage.
   * Contains premise, world rules, continuity axes, and required elements.
   *
   * NOTE, because it is easy to read this type as a promise: the providers assign
   * the WHOLE `StoryBibleResult` here, and TypeScript's excess-property check does
   * not fire on a variable, so the extra fields (`mystery_spine`, `named_entities`,
   * `site_plan`, `planned_twists`, …) ride along into the stored and served novel.
   * That is load-bearing, not an accident — `extendNovel` reads this field back as a
   * full `StoryBibleResult` to continue a finished story with the same canon — so
   * treat none of the bible's fields as generation-only, and remember that anything
   * put on the bible is visible to any client that fetches the novel.
   */
  story_bible?: {
    premise: string;
    core_conflict: string;
    /** The human truth the story explores beneath its plot (optional). */
    theme?: string;
    world_rules: string[];
    required_elements_from_idea: string[];
    forbidden_contradictions: string[];
    continuity_axes: {
      locations: string[];
      inventory_items: string[];
      relationship_keys: string[];
      secret_keys: string[];
      status_flags: string[];
    };
  };
  /** Summary of continuity validation and repair activity during generation. */
  continuity_review?: NovelContinuityReview;
  /** Summary of semantic narrative quality review during generation. */
  semantic_review?: {
    /** Total number of semantic issues found (errors + warnings). */
    issue_count: number;
    /** Number of repair attempts made to fix hard semantic errors. */
    repaired_attempts: number;
    /** All warnings recorded (non-blocking quality observations). */
    warnings: NovelSemanticIssueTrace[];
  };
  /**
   * What the editorial polish pass did, and whether its patch survived the
   * post-polish regression gate. Absent on providers without a polish pass
   * (mock, OpenAI) and on novels generated before the gate existed.
   */
  editorial_polish?: NovelEditorialPolishTrace;
  /**
   * Findings from the detectors currently UNDER MEASUREMENT, as they stood on the
   * manuscript before any repair ran — plus anything a kill switch suppressed. This
   * is the measurement corpus: a new detector's real-world hit rate and
   * false-positive rate are read off finished novels here before it is allowed to
   * cost more tokens, and a detector switched off in prod keeps producing the
   * evidence that says whether to switch it back on. A finding's own `severity`
   * says whether it gated anything; `semantic_review` describes what SHIPPED, this
   * describes what the gates judged. Always present on a real-provider run (empty
   * when nothing was found), so an absent array means "this run predates the
   * channel", not "nothing was found".
   */
  shadow_findings?: NovelSemanticIssueTrace[];
  /**
   * Wall-clock duration of each pipeline stage that ran, in order, for perf
   * tracking and debugging ("what took how long, and where did it fail"). Added
   * by the generation orchestrator; the failing stage (if any) carries the error.
   */
  stage_timings?: GenerationStageTiming[];
  /**
   * Image-rendering breakdown captured during build_playable_novel: how long the
   * cover, backgrounds, and portraits took and how many were rendered/failed.
   * Present only when real images were rendered (absent for placeholder assets).
   */
  image_render?: ImageRenderTrace;
  created_from_job_id?: string;
  /**
   * Real provider cost (in cents) accumulated across this novel's generation
   * pipeline — for novel-level cost visibility. 0 on the mock provider.
   */
  real_cost_cents?: number;
}

/** One pipeline stage's wall-clock timing and outcome. */
export interface GenerationStageTiming {
  /** Pipeline step name (a GenerationStep, or "build_playable_novel"). */
  step: string;
  /** "ok" when the stage completed; "failed" when it threw. */
  status: "ok" | "failed";
  /** ISO timestamp when the stage started. */
  started_at: string;
  /** Wall-clock milliseconds the stage took. */
  duration_ms: number;
  /** Present only on a failed stage: the error message. */
  error?: string;
}

/** Image-rendering breakdown for a single novel build. */
export interface ImageRenderTrace {
  /** Wall-clock ms for the cover image. */
  cover_ms: number;
  /** Wall-clock ms for all background images (their concurrent phase). */
  backgrounds_ms: number;
  /** Wall-clock ms for all character portraits (their concurrent phase). */
  portraits_ms: number;
  /** Number of background images rendered. */
  background_count: number;
  /** Number of portrait images rendered (neutral + emotion variants). */
  portrait_count: number;
  /** Total wall-clock ms for the whole image phase. */
  total_ms: number;
  /** Concurrency cap used for image rendering. */
  concurrency: number;
  /** Number of images the provider returned with status "failed". */
  failures: number;
}

/**
 * A machine translation of a novel's reader-facing content into ONE language,
 * layered on top of the canonical graph (which stays in `Novel.language`).
 * Keyed by the canonical scene / choice / ending / character ids; any id (or
 * field) absent here falls back to the canonical value. Lives at the `Novel`
 * top level — never nested inside `Scene`/`Choice`, which reject unknown keys.
 * Machine-generated, so it is always surfaced with a "double-check" disclaimer.
 */
export interface NovelTranslation {
  title: string;
  description: string;
  tone?: string;
  genre?: string[];
  /** Translated world-state label VALUES, keyed by the same state keys. */
  state_labels?: Record<string, string>;
  scenes: Record<string, { title: string; text: string; choices: Record<string, string> }>;
  endings: Record<string, { title: string; description: string }>;
  characters?: Record<string, { name: string }>;
}

export interface Novel {
  schema_version: 1;
  version: number;
  id: string;
  /**
   * Permanent 6-character link code (`/novel/<slug>-<code>`, `/n/<code>`),
   * minted by storage on first save and never changed (see `novelLinks.ts`).
   * Absent on a novel not saved yet, and on the demo novel.
   */
  short_code?: string;
  title: string;
  description: string;
  visibility: Visibility;
  language: string;
  genre: string[];
  tone: string;
  cover_asset_id: string;
  start_scene_id: string;
  characters: Character[];
  scenes: Scene[];
  endings: Ending[];
  assets: Asset[];
  /**
   * Human-readable labels for world-state keys, in the story's language
   * (e.g. { trust_oleg: "Доверие Олега" }). Used by the player to explain
   * locked choices and to announce state changes. Optional — older novels
   * fall back to prettified key names.
   */
  state_labels?: Record<string, string>;
  /**
   * Optional machine translations of the reader-facing content, keyed by
   * language code (e.g. "ru", "ja"). The canonical graph stays in
   * `Novel.language`; the player layers a translation on top when the reader
   * picks a language present here. Machine-generated — surfaced with a
   * "double-check" disclaimer. Absent means the novel is single-language.
   */
  translations?: Record<string, NovelTranslation>;
  /**
   * Optional author-provided "support me" link (DonationAlerts / Boosty / Ko-fi
   * etc.), shown to readers when they reach an ending. Must be a plain http(s)
   * URL; validated on every save. Absent means the author set no link.
   */
  support_link?: string;
  /**
   * How many continuation ("догенерация") passes have grown this novel past its
   * original one-shot form. Absent or 0 means the novel is untouched. Each pass
   * raises the scene ceiling by `mvpLimits.scenesPerContinuation` so a continued
   * novel can legitimately exceed the base `maxScenes` limit.
   */
  continuation_count?: number;
  owner_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  /**
   * Soft-delete marker: an ISO timestamp set when the owner deletes the novel.
   * When present, the novel is kept in storage but hidden from every listing and
   * read (treated as not-found), never physically removed. Absent = live.
   * A restore path is intentionally not built yet — deletion is one-way for now,
   * but the data is retained so restore can be added later.
   */
  deleted_at?: string;
  generation_input: NovelGenerationInput;
  generation_trace?: NovelGenerationTrace;
  /**
   * Set only on the reader copy the API serves to anyone but the owner
   * (`publicNovelProjection`), which lacks the owner-only fields. Never stored:
   * `validateNovelGraph` refuses a document that carries it.
   */
  public_projection?: true;
}

/** A character as surfaced live during generation (name + traits; no portrait yet). */
export interface GenerationPreviewCharacter {
  id: string;
  name: string;
  traits: string[];
  age?: number;
  gender?: string;
  /**
   * The distinct expressions this character uses across scenes, known once the
   * asset-prompt stage has run (before the portrait images themselves are
   * rendered). The monitor cycles the portrait through these.
   */
  emotions?: Emotion[];
  /** Neutral portrait image URL, once rendered (build stage). */
  portrait_url?: string;
  /** Emotion → rendered portrait URL, once the variant sprites exist. */
  emotion_portraits?: Partial<Record<Emotion, string>>;
}

/** A scene node as surfaced live during generation (real title + role). */
export interface GenerationPreviewScene {
  id: string;
  title: string;
  is_ending: boolean;
  ending_type?: string;
  location?: string;
}

/** A branch edge between two scene nodes. */
export interface GenerationPreviewEdge {
  from: string;
  to: string;
}

/**
 * The REAL intermediate content produced by the pipeline, surfaced live on the
 * generation job so the status UI can show the actual story being built — the
 * real premise, characters, branching graph, and scene prose — appearing stage by
 * stage, instead of a symbolic animation. Every field is optional and fills in as
 * its stage completes: premise/locations after the story bible, characters after
 * character generation, scenes/edges after the graph, scene_texts as scenes are
 * written. Portraits are not here (images render only at the final build stage);
 * the finished novel carries them once the job completes.
 */
export interface GenerationPreview {
  premise?: string;
  locations?: string[];
  characters?: GenerationPreviewCharacter[];
  start_scene_id?: string;
  scenes?: GenerationPreviewScene[];
  edges?: GenerationPreviewEdge[];
  /** Real scene prose keyed by scene id, as each scene is written. */
  scene_texts?: Record<string, string>;
}

export interface GenerationJob {
  id: string;
  novel_id: string;
  status: GenerationJobStatus;
  current_step: GenerationStep;
  progress: number;
  error: string | null;
  created_at: string;
  updated_at: string;
  /** Pipeline log lines emitted during generation — for debug display only. */
  logs?: string[];
  /**
   * Real intermediate content produced so far, for the live generation monitor.
   * Present while a pipeline job runs and fills in stage by stage; absent on
   * translation jobs and before the first content stage completes.
   */
  preview?: GenerationPreview;
}
